import React from 'react';
import fr from 'facet-rest';
import { DialogContent } from 'Views/Page/Content/Modal/Components';
import {
  FDClickableText, FDConfirmationDialog, FDPrimaryActionButton, FDSecondaryActionButton, FDStack,
} from '@facet-wealth/design';
import TitleSection from 'Views/Page/Content/Modal/Components/TitleSection';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import DataContainer from 'Components/UI/DataContainer';
import { MetaCollectionRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaCollectionRenderer';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import { FineTuneResult, FineTuneResultOverrides } from './fineTuneResult';

export const RetirementAgeGoalSeekFineTuneStepLayout = () => {
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();

  const {
    loadModalCollectionByLink,
    clearModalCollection,
    modalResource: resource,
  } = useModalContext();

  const startOverRel = resource.withRelationship(
    fr.relationships.startOver.name,
    (rel) => rel,
    () => null,
  );

  const [running, setRunning] = React.useState(false);

  const handleRun = async () => {
    const topSolution = resource.attr('solutions').resources().head();
    setRunning(true);
    try {
      const resp = await topSolution.submit();
      resource.attr('solutions', resp.resources().head().attr('solutions'));
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setRunning(false);
    }
  };

  const submit = async () => {
    try {
      await resource.submit();
      clearModalCollection();
      refreshPageContentCollection();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const {
    dialogOpen,
    loading,
    handleClose,
    handleSubmit,
    handleOpen,
  } = useConfirmationDialogState({
    closeOnSubmit: true,
    onSubmit: () => { return submit(); },
  });

  const hasSpouse = resource.attr('hasSpouse') === 'true';

  return (
    <>
      <DialogContent>
        <FDStack direction={'column'} spacing={2} paddingX={4} paddingY={2}>
          <TitleSection>
            <MetaControlField attr={'title'} />
          </TitleSection>
          <FDStack direction={'column'} spacing={4} width={'100%'}>
            <FDStack direction={'column'} spacing={4} width={'100%'}>
              <MetaControlField attr={'disclaimer'} />
              <DataContainer
                component={MetaCollectionRenderer}
                componentProps={{
                  collection: resource.attr('solutions'),
                  layout: FineTuneResult,
                  LayoutProps: {
                    desiredEndBalance: parseInt(resource.attr('desiredEndBalance'), 10),
                    desiredEndBalanceDisplayPrompt: resource.meta().field('desiredEndBalanceDisplay').attr('prompt'),
                    desiredEndBalanceDisplay: resource.attr('desiredEndBalanceDisplay'),
                    primaryClientBirthDate: resource.attr('primaryClientBirthDate'),
                    spouseClientBirthDate: resource.attr('spouseClientBirthDate'),
                  },
                  metaOverrideSet: FineTuneResultOverrides,
                }}
                loading={running}
              />
              <FDStack spacing={2} direction={'column'} alignItems={'center'} justifyContent={'center'} width={'100%'}>
                <FDStack spacing={2} direction={'row'} alignItems={'center'} justifyContent={'center'}>
                  <FDSecondaryActionButton
                    onClick={() => handleRun()}
                    size={'sm'}
                    label={resource.attr('refreshButtonLabel')}
                    icon={'refresh'}
                    test={'retirement-age-goal-seek-refresh'}
                    loading={running}
                  />
                  <FDPrimaryActionButton
                    onClick={handleOpen}
                    size={'sm'}
                    label={resource.attr('useButtonLabel')}
                    test={'retirement-age-goal-seek-use-value'}
                    loading={running}
                  />
                </FDStack>
                <FDClickableText
                  label={startOverRel.meta('prompt')}
                  onClick={() => {
                    loadModalCollectionByLink(startOverRel.link('related').url());
                  }}
                  appearance={'minor'}
                  disabled={running}
                />
              </FDStack>
            </FDStack>
          </FDStack>
        </FDStack>
      </DialogContent>
      <FDConfirmationDialog
        message={hasSpouse
          ? 'This will update the members\' Retirement Outcome ages with the selected values.'
          : 'This will update the member\'s Retirement Outcome age with the selected value.'}
        title={'Are you sure?'}
        onClose={handleClose}
        onConfirm={handleSubmit}
        loading={loading}
        open={dialogOpen}
      />
    </>

  );
};
